import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["children", "statusIconAltText"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { forwardRef } from 'react';
import { describe, PropTypes } from '@amzn/stencil-react-desc';
import { styledWithTheme } from "../context";
import { View } from "../layout";
import { opacify, withTestAttributes } from "../utils";
import CardStatusIcon from "./CardStatusIcon";
import { CardDefaultProps, CardStatusBorderColors } from "./constants";
import { CardStatus } from "./interfaces";
import { jsx as ___EmotionJSX } from "@emotion/core";
export var createCardComponent = function createCardComponent(_ref) {
  var additionalStyles = _ref.additionalStyles,
      interactive = _ref.interactive,
      name = _ref.name,
      tag = _ref.tag;
  // The styled component has to be casted to any for the style string
  // to work, since it cannot infer the generic type T if it's used as
  // the type on withComponent
  var CardBase = styledWithTheme(View.withComponent(tag, {
    target: "e1egzu6o0"
  }))(function (_ref2) {
    var _ref2$additionalStyle = _ref2.additionalStyles,
        additionalStyles = _ref2$additionalStyle === void 0 ? {} : _ref2$additionalStyle,
        interactive = _ref2.interactive,
        isElevated = _ref2.isElevated,
        _ref2$status = _ref2.status,
        status = _ref2$status === void 0 ? CardDefaultProps.status : _ref2$status,
        selectors = _ref2.theme.selectors;
    var elevatedStyles = {};

    if (interactive || isElevated) {
      elevatedStyles = {
        boxShadow: "0 0 10px 0 ".concat(opacify(selectors.color('neutral90'), 0.3))
      };
    }

    var borderStyles = {};

    if (interactive || !isElevated) {
      borderStyles = {
        border: "1px solid ".concat(selectors.color('neutral50'))
      };
    }

    var interactiveStyles = {};

    if (interactive) {
      interactiveStyles = {
        transition: 'transform 0.2s',
        ':hover': {
          cursor: 'pointer',
          transform: 'scale(1.08)',
          boxShadow: "0 0 20px 0 ".concat(opacify(selectors.color('neutral90'), 0.3))
        },
        ':focus': {
          outline: "2px solid ".concat(selectors.color('primary70')),
          outlineOffset: '-2px'
        }
      };
    }

    var statusStyles = {};

    if (status !== CardStatus.Default) {
      statusStyles = {
        borderTop: "8px solid ".concat(selectors.color(CardStatusBorderColors[status]))
      };
    }

    return _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
      position: 'relative',
      boxSizing: 'border-box'
    }, additionalStyles), borderStyles), elevatedStyles), interactiveStyles), statusStyles);
  });
  var CardComponentWithoutDocs = /*#__PURE__*/forwardRef(function (_ref3, ref) {
    var children = _ref3.children,
        statusIconAltText = _ref3.statusIconAltText,
        props = _objectWithoutProperties(_ref3, _excluded);

    var status = props.status;

    var cardProps = _objectSpread(_objectSpread({}, props), {}, {
      additionalStyles: additionalStyles,
      interactive: interactive,
      ref: ref
    });

    var iconProps = {
      status: status,
      statusIconAltText: statusIconAltText
    };
    return ___EmotionJSX(CardBase, cardProps, status !== CardStatus.Default && ___EmotionJSX(CardStatusIcon, iconProps), children);
  }); // Force TS to accept .withComponent

  var CardStyled = CardComponentWithoutDocs;

  CardStyled.withComponent = function (newTag) {
    return createCardComponent({
      additionalStyles: additionalStyles,
      interactive: interactive,
      name: name,
      tag: newTag
    });
  };

  var CardWithDocs = describe(CardStyled).description("Card component using <".concat(tag, "> element."));
  CardWithDocs.defaultProps = CardDefaultProps;
  CardWithDocs.propTypes = _objectSpread(_objectSpread({}, View.describedPropTypes), {}, {
    children: PropTypes.node.description('Content to display in the card.'),
    isElevated: PropTypes.bool.description('Determines whether a border or a shadow should be used around a Card.'),
    status: PropTypes.oneOf(Object.values(CardStatus)).description('Determines the color of the border on top of the Card and additional icon in the corner.'),
    statusIconAltText: PropTypes.string.description("Alt text for the icon displayed in the corner of the Card when status is set to a non default one. The text should be tailored to Card's status.")
  });
  var CardWithTestAttributes = withTestAttributes({
    component: "StencilReact".concat(name)
  })(CardWithDocs);
  CardWithTestAttributes.displayName = name;
  return CardWithTestAttributes;
};